import type { DynamicToolUIPart, JSONValue, ProviderMetadata } from "ai";
import { isRecord } from "../../../common/utils/isRecord";

export interface ToolTime {
  seconds: number;
}

const LANGFAST_NAMESPACE = "langfast";

/** `ToolResultOutput` envelope types that mean the tool call failed. */
const ERROR_OUTPUT_TYPES = new Set(["error-text", "error-json", "execution-denied"]);
const OUTPUT_TYPES = new Set(["text", "json", "content", "error-text", "error-json"]);

function stringifyErrorValue(value: unknown): string {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export interface ToolOutput {
  value: unknown;
  /** Set when the tool threw — the message is what the model saw. */
  errorText?: string;
}

/**
 * Unwrap a tool result output.
 *
 * Persisted results carry the AI SDK `ToolResultOutput` envelope
 * (`{ type: "json", value }`), while live stream chunks carry the value the
 * tool returned. Both reach the UI, so both are accepted here.
 */
function readToolOutput(output: unknown): ToolOutput {
  if (!isRecord(output) || typeof output.type !== "string") {
    return { value: output };
  }

  const { type } = output;

  if (type === "execution-denied") {
    return {
      value: undefined,
      errorText: typeof output.reason === "string" ? output.reason : "Tool call execution denied.",
    };
  }

  if (!OUTPUT_TYPES.has(type) || !("value" in output)) {
    return { value: output };
  }

  if (ERROR_OUTPUT_TYPES.has(type)) {
    return { value: undefined, errorText: stringifyErrorValue(output.value) };
  }

  return { value: output.value };
}

/**
 * Wrap a value back into a `ToolResultOutput` envelope, so projecting live UI
 * parts into cached result messages produces the same shape the worker persists.
 */
function toToolResultOutput(value: unknown, errorText?: string): Record<string, unknown> {
  if (errorText != null) {
    return { type: "error-text", value: errorText };
  }
  if (typeof value === "string") {
    return { type: "text", value };
  }
  return { type: "json", value: value ?? null };
}

/** Render a tool input or output for display: strings as-is, everything else as JSON. */
function formatToolPayload(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function parseToolTime(value: unknown): ToolTime | undefined {
  if (!isRecord(value) || typeof value.seconds !== "number" || !Number.isFinite(value.seconds)) {
    return undefined;
  }
  return { seconds: value.seconds };
}

/** Read `tool_time` from a persisted `tool-result` content part. */
function getToolTimeFromContentPart(part: Record<string, unknown>): ToolTime | undefined {
  return parseToolTime(part.tool_time);
}

function getToolTimeFromProviderMetadata(
  providerMetadata: ProviderMetadata | undefined,
): ToolTime | undefined {
  if (!providerMetadata) return undefined;
  const langfast = providerMetadata[LANGFAST_NAMESPACE];
  if (!isRecord(langfast)) return undefined;
  return parseToolTime(langfast.tool_time);
}

/** Build providerMetadata carrying `langfast.tool_time` (merges existing). */
function withToolTimeProviderMetadata(
  tool_time: ToolTime | undefined,
  existing?: ProviderMetadata,
): ProviderMetadata | undefined {
  if (!tool_time) return existing;

  const existingLangfast = isRecord(existing?.[LANGFAST_NAMESPACE])
    ? existing[LANGFAST_NAMESPACE]
    : {};

  return {
    ...existing,
    [LANGFAST_NAMESPACE]: {
      ...existingLangfast,
      tool_time: tool_time as unknown as JSONValue, // `ToolTime` is JSON-safe, but TS doesn't know that
    },
  };
}

function getToolResultProviderMetadata(part: DynamicToolUIPart): ProviderMetadata | undefined {
  return "resultProviderMetadata" in part ? part.resultProviderMetadata : undefined;
}

/**
 * Marks a reloaded `interrupted` chat call in its result metadata. A stored
 * part is shown as an `output-error` (see `toDynamicToolUIPart`) and stored
 * again on every save, so this is how `toStoredToolPart` tells it from a real
 * error, and how `ToolCallLog` shows it the way it did live. Only the client
 * sets it: the worker's result metadata carries `tool_time` alone, and nothing
 * a tool returns or throws can reach it.
 */
function withInterruptedMarker(metadata: ProviderMetadata | undefined): ProviderMetadata {
  const langfast = isRecord(metadata?.[LANGFAST_NAMESPACE]) ? metadata[LANGFAST_NAMESPACE] : {};

  return { ...metadata, [LANGFAST_NAMESPACE]: { ...langfast, interrupted: true } };
}

function isMarkedInterrupted(part: DynamicToolUIPart): boolean {
  const langfast = getToolResultProviderMetadata(part)?.[LANGFAST_NAMESPACE];

  return isRecord(langfast) && langfast.interrupted === true;
}

/** Duration of a tool call, when the worker stamped one onto the result. */
function getToolTimeSeconds(part: DynamicToolUIPart): number | undefined {
  return getToolTimeFromProviderMetadata(getToolResultProviderMetadata(part))?.seconds;
}

export {
  formatToolPayload,
  getToolResultProviderMetadata,
  getToolTimeFromContentPart,
  getToolTimeFromProviderMetadata,
  getToolTimeSeconds,
  isMarkedInterrupted,
  readToolOutput,
  toToolResultOutput,
  withInterruptedMarker,
  withToolTimeProviderMetadata,
};
